// src/components/Consolidado.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShoppingBag, DollarSign, Calendar, Copy, Check, Users, TrendingUp, ArrowUp, ArrowDown, Search, X, Image as ImageIcon, ExternalLink, Wallet, ChevronDown, Package } from 'lucide-react';
import apiService from '../services/apiService';
import html2canvas from 'html2canvas';
// import { errorLog, debugLog, warningLog } from '../config/featureFlags';

const Consolidado = ({ user, selectedTrip, onTripSelect, onSelectedTripsChange }) => {
  // Estados principales
  const [trips, setTrips] = useState([]);
  const [selectedTrips, setSelectedTrips] = useState([]); // Array de IDs de viajes seleccionados
  const [envivos, setEnvivos] = useState([]);
  const [selectedEnvivos, setSelectedEnvivos] = useState([]); // Array de IDs de en vivos seleccionados
  // Estados clients y orders se mantienen para compatibilidad pero no se leen directamente
  const [, setClients] = useState([]);
  const [, setOrders] = useState([]);
  
  // Estados para datos consolidados
  const [consolidatedData, setConsolidatedData] = useState({
    totalRevenue: 0,
    totalPaid: 0,
    totalRemaining: 0,
    totalPayments: 0,
    totalOrders: 0,
    totalClients: 0
  });
  
  // Estados para gestión de copiar
  const [copiedOrders, setCopiedOrders] = useState(new Set());
  const [copyMenuOrderId, setCopyMenuOrderId] = useState(null);
  
  // Estados generales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Estado para botón "Volver arriba" y control de scroll
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(true);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Estado para almacenar pagos de cada orden (no usado actualmente)
  // const [orderPayments, setOrderPayments] = useState({});
  
  // Estado para órdenes agrupadas por cliente
  const [consolidatedOrderCards, setConsolidatedOrderCards] = useState([]);
  
  // Estado para items que están siendo actualizados (check_carry_on)
  const [updatingItems, setUpdatingItems] = useState(new Set());

  // Estado para controlar acordeones de productos (abiertos/cerrados)
  const [openProductAccordions, setOpenProductAccordions] = useState(new Set());

  // Orden de las tarjetas por cantidad de items: 'asc' | 'desc'
  const [sortByItemsOrder, setSortByItemsOrder] = useState('desc');

  // Estado para controlar carga continua de envivos
  const [isLoadingContinuously, setIsLoadingContinuously] = useState(false);
  const [continuousLoadingProgress, setContinuousLoadingProgress] = useState({ current: 0, total: 0 });
  
  // Estados para modal de pagos
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCardForPayments, setSelectedCardForPayments] = useState(null);
  const [paymentModalData, setPaymentModalData] = useState({
    totalAdvanceBalance: 0,
    advanceBalances: [],
    payments: [],
    loading: false
  });

  // Estados para modal de imagen de comprobante
  const [showReceiptImageModal, setShowReceiptImageModal] = useState(false);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [receiptImageLoading, setReceiptImageLoading] = useState(false);
  
  // Colores para identificar cada envivo (26 colores distintos y bien diferenciados)
  const envivoColors = [
    // Serie 1: Colores primarios y secundarios (tinte 50, borde 300)
    'bg-blue-50 border-blue-300',
    'bg-green-50 border-green-300',
    'bg-yellow-50 border-yellow-300',
    'bg-purple-50 border-purple-300',
    'bg-pink-50 border-pink-300',
    'bg-indigo-50 border-indigo-300',
    'bg-orange-50 border-orange-300',
    'bg-red-50 border-red-300',
    // Serie 2: Colores complementarios (tinte 50, borde 300)
    'bg-teal-50 border-teal-300',
    'bg-cyan-50 border-cyan-300',
    'bg-emerald-50 border-emerald-300',
    'bg-lime-50 border-lime-300',
    'bg-amber-50 border-amber-300',
    'bg-rose-50 border-rose-300',
    'bg-violet-50 border-violet-300',
    'bg-fuchsia-50 border-fuchsia-300',
    'bg-sky-50 border-sky-300',
    // Serie 3: Variaciones más intensas (tinte 100, borde 400) para mayor contraste
    'bg-blue-100 border-blue-400',
    'bg-green-100 border-green-400',
    'bg-purple-100 border-purple-400',
    'bg-pink-100 border-pink-400',
    'bg-indigo-100 border-indigo-400',
    'bg-orange-100 border-orange-400',
    'bg-red-100 border-red-400',
    'bg-teal-100 border-teal-400',
    'bg-emerald-100 border-emerald-400',
  ];
  
  // Cargar viajes
  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      setTrips(Array.isArray(data) ? data : []);
    } catch (err) {
      // errorLog('Error cargando viajes:', err);
      setTrips([]);
    }
  };
  
  // Cargar en vivos de los viajes seleccionados
  const loadEnvivos = useCallback(async () => {
    try {
      if (selectedTrips.length > 0) {
        // Cargar en vivos de todos los viajes seleccionados
        const promises = selectedTrips.map(tripId => 
          apiService.envivo.getByTrip(tripId)
        );
        const results = await Promise.all(promises);
        
        // Asignar tripId a cada envivo según el viaje del que proviene
        const allEnvivos = [];
        results.forEach((envivosArray, index) => {
          const tripId = selectedTrips[index];
          envivosArray.forEach(envivo => {
            allEnvivos.push({
              ...envivo,
              tripId: envivo.tripId || envivo.trip_id || tripId // Asegurar que tenga tripId
            });
          });
        });
        
        setEnvivos(allEnvivos);
        // debugLog('✅ Envivos cargados con tripId:', allEnvivos.map(e => ({ id: e.id, name: e.name, tripId: e.tripId || e.trip_id })));
      } else {
        setEnvivos([]);
      }
    } catch (err) {
      // errorLog('Error cargando en vivos:', err);
      setEnvivos([]);
    }
  }, [selectedTrips]);
  
  // Función para cargar datos de un envivo individual
  const loadEnvivoData = async (envivoId, envivoColorMap) => {
    const envivo = envivos.find(e => e.id === envivoId);
    // Intentar obtener tripId del envivo o de la orden
    const envivoTripId = envivo?.tripId || envivo?.trip_id || null;
    
    // Cargar órdenes del envivo
    const orders = await apiService.order.getByEnvivo(envivoId);
    const ordersWithEnvivoInfo = orders.map(order => {
      // El trip_id puede venir del backend en la orden, del envivo, o ser null
      const orderTripId = order.trip_id || order.tripId || envivoTripId;
      
      return {
        ...order,
        envivo_id: envivoId,
        trip_id: orderTripId, // Usar el de la orden si existe, sino el del envivo
        envivoColor: envivoColorMap[envivoId]
      };
    });
    
    // Cargar clientes del envivo
    const clientsData = await apiService.client.getByEnvivo(envivoId);
    const clients = Array.isArray(clientsData) ? clientsData : (clientsData.clients || []);
    
    // Cargar pagos para cada orden del envivo
    const paymentsData = {};
    for (const order of ordersWithEnvivoInfo) {
      try {
        const payments = await apiService.payment.getByOrder(order.id);
        paymentsData[order.id] = payments || [];
      } catch (err) {
        // console.error(`Error cargando pagos para orden ${order.id}:`, err);
        paymentsData[order.id] = [];
      }
    }
    
    return {
      orders: ordersWithEnvivoInfo,
      clients,
      paymentsData
    };
  };
  
  // Función para consolidar datos acumulados
  const consolidateAccumulatedData = async (allOrders, allClients, allPaymentsData, envivoColorMap, envivosList = []) => {
    // debugLog('🚀 INICIO consolidateAccumulatedData - Obteniendo bolsas de abonos', {
    //   totalOrders: allOrders.length,
    //   totalClients: allClients.length,
    //   totalPayments: Object.keys(allPaymentsData).length
    // });
    
    const uniqueClients = Array.from(
      new Map(allClients.map(client => [client.id, client])).values()
    );
    
    // Agrupar órdenes por cliente y consolidar
    const ordersByClient = {};
    
    // 🔍 DEBUG: Filtrar órdenes del cliente específico para debugging
    // const debugClientUsername = 'otero.yelis';
    // const debugOrders = allOrders.filter(o => o.client_username === debugClientUsername);
    // if (debugOrders.length > 0) {
    //   debugLog(`🔍 DEBUG - Órdenes encontradas para ${debugClientUsername}:`, {
    //     totalOrders: debugOrders.length,
    //     orders: debugOrders.map(o => ({
    //       orderId: o.id,
    //       envivo_id: o.envivo_id,
    //       envivoName: envivos.find(e => e.id === o.envivo_id)?.name || 'N/A',
    //       total_amount: o.total_amount,
    //       itemsCount: o.items?.length || 0,
    //       items: o.items?.map(i => ({
    //         name: i.productName || i.product_name,
    //         price: i.price,
    //         quantity: i.quantity,
    //         subtotal: parseFloat(i.price || 0) * (i.quantity || 1)
    //       })) || []
    //     }))
    //   });
    // }
    
    allOrders.forEach(order => {
      const key = order.client_username || order.client_id;
      if (!ordersByClient[key]) {
        // 🔍 DEBUG: Log para verificar estructura de orden
        // if (!order.client_id && order.clientId) {
        //   debugLog(`⚠️ Orden tiene clientId en camelCase, no client_id:`, {
        //     orderId: order.id,
        //     clientId: order.clientId,
        //     client_id: order.client_id,
        //     client_username: order.client_username
        //   });
        // }
        
        ordersByClient[key] = {
          client: uniqueClients.find(c => c.username === order.client_username),
          clientId: order.client_id || order.clientId, // Intentar ambos formatos
          clientUsername: order.client_username,
          tripId: order.trip_id || order.tripId, // Mantener por compatibilidad (primer viaje)
          tripIds: new Set(), // Set para guardar todos los viajes únicos
          orders: [],
          allItems: [],
          totalAmount: 0,
          payments: []
        };
      }
      
      ordersByClient[key].orders.push(order);
      
      // Agregar el trip_id al set de viajes únicos (intentar ambos formatos)
      const tripId = order.trip_id || order.tripId;
      if (tripId) {
        ordersByClient[key].tripIds.add(tripId);
      }
      // else {
      //   debugLog(`⚠️ Orden sin trip_id:`, {
      //     orderId: order.id,
      //     client_username: order.client_username,
      //     trip_id: order.trip_id,
      //     tripId: order.tripId,
      //     envivo_id: order.envivo_id
      //   });
      // }
      
      const items = (order.items || []).map(item => ({
        ...item,
        envivoColor: envivoColorMap[order.envivo_id],
        check_carry_on: item.check_carry_on === true || item.check_carry_on === 1 ? true : false
      }));
      ordersByClient[key].allItems.push(...items);
      
      // Calcular total sumando items directamente (precio * cantidad) para evitar errores del backend
      const orderTotalFromItems = items.reduce((sum, item) => {
        return sum + (parseFloat(item.price || 0) * (item.quantity || 1));
      }, 0);
      
      // Usar el total calculado de items, o el total_amount del backend si no hay items
      const orderTotal = orderTotalFromItems > 0 ? orderTotalFromItems : parseFloat(order.total_amount || 0);
      
      // debugLog(`💰 Cliente: ${order.client_username}, Orden: ${order.id}`, {
      //   orderTotalFromItems,
      //   orderTotalAmount: order.total_amount,
      //   itemsCount: items.length,
      //   envivo_id: order.envivo_id,
      //       envivoName: envivosList.find(e => e.id === order.envivo_id)?.name || 'N/A',
      //   itemsSum: items.map(i => `${i.productName}: $${parseFloat(i.price || 0) * (i.quantity || 1)}`),
      //   finalTotal: orderTotal
      // });
      
      // 🔍 DEBUG específico para otero.yelis
      // const debugClientUsername = 'otero.yelis';
      // if (order.client_username === debugClientUsername) {
      //   debugLog(`🔍 DEBUG - Sumando orden a ${debugClientUsername}:`, {
      //     orderId: order.id,
      //     envivo_id: order.envivo_id,
      //       envivoName: envivosList.find(e => e.id === order.envivo_id)?.name || 'N/A',
      //     orderTotal,
      //     totalAmountAntes: ordersByClient[key].totalAmount,
      //     totalAmountDespues: ordersByClient[key].totalAmount + orderTotal
      //   });
      // }
      
      ordersByClient[key].totalAmount += orderTotal;
      
      ordersByClient[key].payments.push(...(allPaymentsData[order.id] || []));
    });
    
    // Convertir a array de cards consolidadas y obtener bolsas de abonos
    const cards = await Promise.all(Object.values(ordersByClient).map(async (clientData) => {
      const uniquePayments = Array.from(
        new Map(clientData.payments.map(p => [p.id, p])).values()
      );
      // ⚠️ EXCLUIR pagos con "Saldo a Favor" para evitar doble conteo
      // Los pagos de "Saldo a Favor" representan dinero que ya fue contabilizado
      // en el pago original que ingresó a la bolsa de abonos
      const saldoAFavorPayments = [];
      const paymentsExcludingSaldoAFavor = uniquePayments.filter(payment => {
        const bankName = (payment.bank_name || '').toLowerCase().trim();
        const paymentType = (payment.payment_type || '').toLowerCase().trim();
        const saldoAFavor = 'saldo a favor';
        const isSaldoAFavor = bankName === saldoAFavor || paymentType === saldoAFavor;
        
        if (isSaldoAFavor) {
          saldoAFavorPayments.push(payment);
        }
        
        return !isSaldoAFavor;
      });
      
      // 🚨 LOG: Identificar clientes con pagos "Saldo a Favor"
      // if (saldoAFavorPayments.length > 0) {
      //   const totalSaldoAFavor = saldoAFavorPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      //   console.log(`[SALDO A FAVOR] Cliente: ${clientData.clientUsername} | Pagos excluidos: ${saldoAFavorPayments.length} | Total excluido: $${totalSaldoAFavor.toLocaleString('es-CO')}`);
      // }
      
      let totalPaid = paymentsExcludingSaldoAFavor.reduce((sum, payment) => 
        sum + parseFloat(payment.amount || 0) + parseFloat(payment.excess_to_advance || 0), 0
      );
      
      // ✅ Obtener bolsas de abonos (una vez por cada viaje) - SOLO para referencia, NO se suman al total pagado
      // La bolsa es un sobrante de pagos anteriores que aún no se ha aplicado a órdenes
      const uniqueTripIds = Array.from(clientData.tripIds);
      let totalAdvancePayments = 0;
      
      // 🔍 DEBUG: Log para verificar datos del cliente
      // debugLog(`🔍 DEBUG BOLSA - Cliente: ${clientData.clientUsername}`, {
      //   clientId: clientData.clientId,
      //   uniqueTripIds: uniqueTripIds,
      //   tripIdsSize: clientData.tripIds.size,
      //   hasClientId: !!clientData.clientId,
      //   hasTripIds: uniqueTripIds.length > 0
      // });
      
      for (const tripId of uniqueTripIds) {
        try {
          if (clientData.clientId && tripId) {
            // debugLog(`🚀 Intentando obtener bolsa para cliente ${clientData.clientUsername} (ID: ${clientData.clientId}) en viaje ${tripId}`);
            const balanceData = await apiService.advancePayment.getBalance(clientData.clientId, tripId);
            // debugLog(`✅ Respuesta de bolsa recibida:`, balanceData);
            
            // Intentar diferentes estructuras de respuesta del balance
            const balance = parseFloat(
              balanceData.summary?.total_available || 
              balanceData.balance || 
              balanceData.available_balance || 
              0
            );
            // debugLog(`💰 Balance calculado: ${balance}`);
            
            if (balance > 0) {
              totalAdvancePayments += balance;
              // debugLog(`💰 Bolsa encontrada para cliente ${clientData.clientUsername} en viaje ${tripId}: $${balance.toLocaleString('es-CO')}`);
            }
            // else {
            //   debugLog(`⚠️ Balance es 0 o negativo para cliente ${clientData.clientUsername} en viaje ${tripId}`);
            // }
          }
          // else {
          //   debugLog(`⚠️ No se puede obtener bolsa - Cliente: ${clientData.clientUsername}`, {
          //     clientId: clientData.clientId,
          //     tripId: tripId,
          //     tieneClientId: !!clientData.clientId,
          //     tieneTripId: !!tripId
          //   });
          // }
        } catch (err) {
          // errorLog(`❌ Error obteniendo bolsa para cliente ${clientData.clientUsername} en viaje ${tripId}:`, err);
          // Continuar con otros viajes aunque falle uno
        }
      }
      
      // debugLog(`📊 Total bolsas disponibles para ${clientData.clientUsername}: $${totalAdvancePayments.toLocaleString('es-CO')}`);
      
      // ⚠️ IMPORTANTE: NO sumar la bolsa al total pagado
      // La bolsa de abonos es un "sobrante" de pagos anteriores que aún no se ha usado
      // Si ya sumamos los pagos (abonos), no debemos sumar también la bolsa porque sería doble conteo
      // La bolsa solo se muestra para referencia, pero NO se incluye en el cálculo del total "Abonado"
      
      const deliveryStatus = clientData.orders[0]?.delivery_status || '';
      
      // Recalcular totalAmount desde items para asegurar precisión
      const recalculatedTotalAmount = clientData.allItems.reduce((sum, item) => {
        return sum + (parseFloat(item.price || 0) * (item.quantity || 1));
      }, 0);
      
      // Usar el total recalculado o el acumulado (el recalculado debería ser más preciso)
      const finalTotalAmount = recalculatedTotalAmount > 0 ? recalculatedTotalAmount : clientData.totalAmount;
      
      const carryOnExtra = clientData.allItems.reduce((sum, item) => {
        if (item.carry_on === 1) {
          return sum + (parseFloat(item.price || 0) * (item.quantity || 1));
        }
        return sum;
      }, 0);
      
      const minPayment = 0.5 * (carryOnExtra + finalTotalAmount);
      
      // debugLog(`📊 Total recalculado para ${clientData.clientUsername}:`, {
      //   totalAmountAcumulado: clientData.totalAmount,
      //   totalAmountRecalculado: recalculatedTotalAmount,
      //   finalTotalAmount,
      //   itemsCount: clientData.allItems.length,
      //   ordersCount: clientData.orders.length,
      //   ordersBreakdown: clientData.orders.map(o => ({
      //     orderId: o.id,
      //     envivo_id: o.envivo_id,
      //     envivoName: envivos.find(e => e.id === o.envivo_id)?.name || 'N/A',
      //     total_amount: o.total_amount,
      //     itemsCount: o.items?.length || 0
      //   })),
      //   itemsBreakdown: clientData.allItems.map(i => ({
      //     name: i.productName,
      //     price: i.price,
      //     quantity: i.quantity,
      //     subtotal: parseFloat(i.price || 0) * (i.quantity || 1),
      //     envivoColor: i.envivoColor ? 'SI' : 'NO'
      //   }))
      // });
      
      // 🔍 DEBUG específico para otero.yelis
      // if (clientData.clientUsername === 'otero.yelis') {
      //   debugLog(`🔍🔍🔍 DEBUG DETALLADO otero.yelis:`, {
      //     totalOrders: clientData.orders.length,
      //     orders: clientData.orders.map(o => ({
      //       orderId: o.id,
      //       envivo_id: o.envivo_id,
      //       envivoName: envivos.find(e => e.id === o.envivo_id)?.name || 'N/A',
      //       total_amount: o.total_amount,
      //       items: o.items?.map(i => ({
      //         name: i.productName || i.product_name,
      //         price: i.price,
      //         quantity: i.quantity,
      //         subtotal: parseFloat(i.price || 0) * (i.quantity || 1)
      //       })) || []
      //     })),
      //     totalItems: clientData.allItems.length,
      //     allItems: clientData.allItems.map(i => ({
      //       name: i.productName,
      //       price: i.price,
      //       quantity: i.quantity,
      //       subtotal: parseFloat(i.price || 0) * (i.quantity || 1),
      //       envivoColor: i.envivoColor || 'SIN COLOR'
      //     })),
      //     totalAmountAcumulado: clientData.totalAmount,
      //     totalAmountRecalculado: recalculatedTotalAmount,
      //     finalTotalAmount
      //   });
      // }
      
      return {
        client: clientData.client,
        clientId: clientData.clientId,
        clientUsername: clientData.clientUsername,
        tripId: clientData.tripId, // Mantener para compatibilidad
        tripIds: uniqueTripIds, // Todos los viajes únicos
        items: clientData.allItems,
        totalAmount: finalTotalAmount,
        totalPaid,
        remainingAmount: finalTotalAmount - totalPaid,
        orders: clientData.orders,
        deliveryStatus,
        carryOnExtra,
        minPayment,
        advancePaymentsTotal: totalAdvancePayments // Guardar para referencia
      };
    }));
    
    // Calcular datos consolidados - usar el total de las cards que ya está recalculado
    const totalRevenue = cards.reduce((sum, card) => sum + card.totalAmount, 0);
    
    const totalPaid = cards.reduce((sum, card) => sum + card.totalPaid, 0);
    
    const totalPayments = Object.values(allPaymentsData).reduce((sum, payments) => 
      sum + (payments || []).length, 0
    );
    
    return {
      cards,
      consolidatedData: {
        totalRevenue,
        totalPaid,
        totalRemaining: totalRevenue - totalPaid,
        totalPayments,
        totalOrders: allOrders.length,
        totalClients: cards.length
      },
      uniqueClients
    };
  };
  
  // Cargar datos consolidados cuando se seleccionan en vivos
  useEffect(() => {
    if (selectedEnvivos.length === 0) {
      setOrders([]);
      setClients([]);
      setConsolidatedOrderCards([]);
      setConsolidatedData({
        totalRevenue: 0,
        totalPaid: 0,
        totalRemaining: 0,
        totalPayments: 0,
        totalOrders: 0,
        totalClients: 0
      });
      return;
    }
    
    const loadConsolidatedData = async () => {
      // Si está en modo carga continua, no ejecutar (se maneja en selectAllEnvivos)
      if (isLoadingContinuously) {
        return;
      }
      
      setLoading(true);
      try {
        // Crear mapa de envivos con sus colores
        const envivoColorMap = {};
        selectedEnvivos.forEach((envivoId, index) => {
          envivoColorMap[envivoId] = envivoColors[index % envivoColors.length];
        });
        
        // Cargar órdenes de todos los en vivos seleccionados
        const ordersPromises = selectedEnvivos.map(envivoId => 
          apiService.order.getByEnvivo(envivoId)
        );
        const ordersArrays = await Promise.all(ordersPromises);
        
        // Aplanar todos los arrays de órdenes con información del envivo
        const allOrders = [];
        ordersArrays.forEach((orders, envivoIndex) => {
          const envivoId = selectedEnvivos[envivoIndex];
          // Buscar el envivo completo para obtener el tripId
          const envivo = envivos.find(e => e.id === envivoId);
          // Intentar obtener tripId del envivo (ahora debería tenerlo)
          const envivoTripId = envivo?.tripId || envivo?.trip_id || null;
          
          // debugLog(`📦 Cargando órdenes del envivo: ${envivo?.name || envivoId}`, {
          //   envivoId,
          //   envivoName: envivo?.name,
          //   ordersCount: orders?.length || 0,
          //   orders: orders?.map(o => ({
          //     id: o.id,
          //     client_username: o.client_username,
          //     total_amount: o.total_amount,
          //     itemsCount: o.items?.length || 0
          //   }))
          // });
          
          // 🔍 DEBUG: Verificar envivo y tripId
          // if (!envivoTripId && orders.length > 0) {
          //   debugLog(`⚠️ No se encontró tripId para envivo ${envivoId}:`, {
          //     envivoId,
          //     envivo: envivo,
          //     envivoTripId: envivo?.tripId,
          //     envivoTrip_id: envivo?.trip_id,
          //     primeraOrder: {
          //       orderId: orders[0].id,
          //       orderTrip_id: orders[0].trip_id,
          //       orderTripId: orders[0].tripId
          //     }
          //   });
          // } else if (envivoTripId) {
          //   debugLog(`✅ Envivo ${envivoId} tiene tripId: ${envivoTripId}`);
          // }
          
          orders.forEach(order => {
            // El trip_id puede venir del backend en la orden, del envivo, o ser null
            const orderTripId = order.trip_id || order.tripId || envivoTripId;
            
            // if (!orderTripId) {
            //   debugLog(`⚠️ Orden ${order.id} sin trip_id - envivo ${envivoId} también sin tripId`);
            // }
            
            allOrders.push({
              ...order,
              envivo_id: envivoId,
              trip_id: orderTripId, // Usar el de la orden si existe, sino el del envivo
              envivoColor: envivoColorMap[envivoId]
            });
          });
        });
        setOrders(allOrders);
        
        // Cargar clientes únicos
        const clientsPromises = selectedEnvivos.map(envivoId => 
          apiService.client.getByEnvivo(envivoId)
        );
        const clientsArrays = await Promise.all(clientsPromises);
        
        const allClientsArrays = clientsArrays.map(data => 
          Array.isArray(data) ? data : (data.clients || [])
        );
        const allClients = allClientsArrays.flat();
        
        const uniqueClients = Array.from(
          new Map(allClients.map(client => [client.id, client])).values()
        );
        setClients(uniqueClients);
        
        // Cargar pagos para cada orden
        const paymentsData = {};
        for (const order of allOrders) {
          try {
            const payments = await apiService.payment.getByOrder(order.id);
            paymentsData[order.id] = payments || [];
          } catch (err) {
            // console.error(`Error cargando pagos para orden ${order.id}:`, err);
            paymentsData[order.id] = [];
          }
        }
        // setOrderPayments(paymentsData); // Comentado - no usado actualmente
        
        // ✅ Usar la función de consolidación que ya incluye las bolsas
        const consolidated = await consolidateAccumulatedData(
          allOrders,
          allClients,
          paymentsData,
          envivoColorMap,
          envivos
        );
        
        setConsolidatedOrderCards(consolidated.cards);
        setConsolidatedData(consolidated.consolidatedData);
        
      } catch (err) {
        // errorLog('Error cargando datos consolidados:', err);
        setError('Error al cargar datos consolidados');
      } finally {
        setLoading(false);
      }
    };
    
    loadConsolidatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvivos, envivos, isLoadingContinuously]);
  
  // Función para seleccionar todos los envivos y cargarlos secuencialmente
  const selectAllEnvivos = async () => {
    if (envivos.length === 0) return;
    
    // Seleccionar todos los envivos
    const allEnvivoIds = envivos.map(e => e.id);
    setSelectedEnvivos(allEnvivoIds);
    
    // Iniciar carga continua
    setIsLoadingContinuously(true);
    setLoading(true);
    setContinuousLoadingProgress({ current: 0, total: allEnvivoIds.length });
    
    try {
      // Crear mapa de envivos con sus colores
      const envivoColorMap = {};
      allEnvivoIds.forEach((envivoId, index) => {
        envivoColorMap[envivoId] = envivoColors[index % envivoColors.length];
      });
      
      // Acumuladores para datos
      let allOrders = [];
      let allClients = [];
      let allPaymentsData = {};
      
      // Cargar cada envivo secuencialmente
      for (let i = 0; i < allEnvivoIds.length; i++) {
        const envivoId = allEnvivoIds[i];
        setContinuousLoadingProgress({ current: i + 1, total: allEnvivoIds.length });
        
        // Cargar datos del envivo
        const envivoData = await loadEnvivoData(envivoId, envivoColorMap);
        
        // Acumular datos
        allOrders = [...allOrders, ...envivoData.orders];
        allClients = [...allClients, ...envivoData.clients];
        allPaymentsData = { ...allPaymentsData, ...envivoData.paymentsData };
        
        // Consolidar y actualizar estado después de cada envivo
        const consolidated = await consolidateAccumulatedData(
          allOrders,
          allClients,
          allPaymentsData,
          envivoColorMap,
          envivos
        );
        
        setOrders(allOrders);
        setClients(consolidated.uniqueClients);
        setConsolidatedOrderCards(consolidated.cards);
        setConsolidatedData(consolidated.consolidatedData);
        
        // Pequeña pausa para permitir que la UI se actualice
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } catch (err) {
      // errorLog('Error cargando envivos de forma continua:', err);
      setError('Error al cargar envivos: ' + err.message);
    } finally {
      setLoading(false);
      setIsLoadingContinuously(false);
      setContinuousLoadingProgress({ current: 0, total: 0 });
    }
  };
  
  // Cargar viajes al montar
  useEffect(() => {
    loadTrips();
  }, []);

  // Notificar cambios en selectedTrips al componente padre
  useEffect(() => {
    if (onSelectedTripsChange) {
      onSelectedTripsChange(selectedTrips);
    }
  }, [selectedTrips, onSelectedTripsChange]);
  
  // Mostrar/ocultar botón "Volver arriba" y buscador móvil según scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobileOrTablet = window.innerWidth < 1024; // Mobile y tablet/iPad
      
      // Umbral para mostrar el botón y buscador: 200px desde el inicio
      const SHOW_THRESHOLD = 200;
      
      if (currentScrollY > SHOW_THRESHOLD) {
        setShowScrollTop(true);
        if (isMobileOrTablet) {
          setShowMobileSearch(true);
        } else {
          setShowMobileSearch(true);
        }
      } else {
        setShowScrollTop(false);
        if (isMobileOrTablet) {
          setShowMobileSearch(false);
        } else {
          setShowMobileSearch(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Función para limpiar resaltados anteriores
  const clearHighlights = useCallback(() => {
    const containers = document.querySelectorAll('.search-highlight-container');
    containers.forEach(container => {
      const text = container.textContent;
      const textNode = document.createTextNode(text);
      if (container.parentNode) {
        container.parentNode.replaceChild(textNode, container);
      }
    });
    document.body.normalize();
  }, []);

  // Función para resaltar texto en la página
  const highlightText = useCallback((term) => {
    if (!term || typeof window === 'undefined') return;
    
    // Limpiar resaltados anteriores
    clearHighlights();

    // Primero verificar si el término existe en el contenido usando window.find
    let windowFound = false;
    try {
      windowFound = window.find(term, false, false, true, false, true, false);
    } catch (e) {
      // Ignorar errores
    }

    // Si window.find no encuentra nada, mostrar alerta
    if (!windowFound) {
      const allText = (document.body.innerText || document.body.textContent || '').toLowerCase();
      if (!allText.includes(term.toLowerCase())) {
        alert(`No se encontraron coincidencias para "${term}".`);
        return;
      }
    }

    // Buscar y resaltar todas las ocurrencias en nodos de texto
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          if (
            parent.tagName === 'INPUT' || 
            parent.tagName === 'TEXTAREA' || 
            parent.tagName === 'SCRIPT' || 
            parent.tagName === 'STYLE' ||
            parent.closest('.search-highlight-container') ||
            parent.closest('nav') ||
            parent.closest('button')
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          
          if (node.textContent.trim().length === 0) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );

    const textNodes = [];
    let node;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    let found = false;
    
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];
      const text = textNode.textContent;
      
      regex.lastIndex = 0;
      if (regex.test(text)) {
        found = true;
        regex.lastIndex = 0;
        const highlightedText = text.replace(regex, '<mark class="search-highlight bg-yellow-300 text-gray-900 px-0.5 rounded">$1</mark>');
        
        const wrapper = document.createElement('span');
        wrapper.className = 'search-highlight-container';
        wrapper.innerHTML = highlightedText;
        
        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(wrapper, textNode);
        }
      }
    }

    if (windowFound && !found) {
      // El texto existe pero no lo encontramos en nodos de texto directos
    } else if (found) {
      try {
        window.find(term, false, false, true, false, true, false);
      } catch (e) {
        // Ignorar errores
      }
    }
  }, [clearHighlights]);

  const handleMobileQuickSearch = useCallback(() => {
    if (typeof window === 'undefined') return;
    const term = mobileSearchTerm.trim();
    if (!term) {
      clearHighlights();
      return;
    }

    highlightText(term);
  }, [mobileSearchTerm, highlightText, clearHighlights]);

  // Cargar en vivos cuando cambian los viajes seleccionados
  useEffect(() => {
    loadEnvivos();
  }, [loadEnvivos]);
  
  // Función auxiliar para obtener el color de un envivo
  const getEnvivoColor = (envivoId) => {
    const index = selectedEnvivos.indexOf(envivoId);
    if (index === -1) return '';
    return envivoColors[index % envivoColors.length];
  };

  // Obtener clientes con bolsa de abonos (usando datos ya cargados)
  const clientsWithBag = useMemo(() => {
    return consolidatedOrderCards
      .filter(card => card.advancePaymentsTotal && card.advancePaymentsTotal > 0)
      .map(card => ({
        username: card.clientUsername,
        bagAmount: card.advancePaymentsTotal
      }))
      .sort((a, b) => b.bagAmount - a.bagAmount); // Ordenar por monto descendente
  }, [consolidatedOrderCards]);

  // Tarjetas ordenadas por cantidad de items
  const sortedOrderCards = useMemo(() => {
    const getItemsCount = (card) =>
      (card.items || []).reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
    return [...consolidatedOrderCards].sort((a, b) => {
      const countA = getItemsCount(a);
      const countB = getItemsCount(b);
      return sortByItemsOrder === 'asc' ? countA - countB : countB - countA;
    });
  }, [consolidatedOrderCards, sortByItemsOrder]);

  // Función para obtener el label del estado del viaje
  const getDeliveryStatusLabel = (status) => {
    const statusMap = {
      '': 'Estado inicial',
      'barrido': 'Revisado',
      'completo': 'Completo',
      'notificado': 'Cliente notificado',
      'alistar': 'Para alistar',
      'preparado': 'Ya preparado',
      'enviado': 'Enviado al cliente',
      'abandonado': 'Abandonado'
    };
    return statusMap[status] || 'Sin estado';
  };
  
  // Función para obtener el color del estado del viaje
  const getDeliveryStatusColor = (status) => {
    const colorMap = {
      '': 'bg-gray-100 text-gray-700 border-gray-200',
      'barrido': 'bg-blue-100 text-blue-800 border-blue-200',
      'completo': 'bg-green-100 text-green-800 border-green-200',
      'notificado': 'bg-purple-100 text-purple-800 border-purple-200',
      'alistar': 'bg-orange-100 text-orange-800 border-orange-200',
      'preparado': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'enviado': 'bg-teal-100 text-teal-800 border-teal-200',
      'abandonado': 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };
  
  // Toggle selección de viaje
  const toggleTripSelection = (tripId) => {
    setSelectedTrips(prev => {
      if (prev.includes(tripId)) {
        return prev.filter(id => id !== tripId);
      } else {
        return [...prev, tripId];
      }
    });
    // La notificación a App.jsx se hace automáticamente en el useEffect
  };
  
  // Toggle selección de en vivo
  const toggleEnvivoSelection = (envivoId) => {
    setSelectedEnvivos(prev => {
      if (prev.includes(envivoId)) {
        return prev.filter(id => id !== envivoId);
      } else {
        return [...prev, envivoId];
      }
    });
  };
  
  // Función para copiar pedido al portapapeles
  const copyOrderToClipboard = async (card, isConsolidated = false) => {
    try {
      const date = new Date().toLocaleDateString('es-CO');
      const envivoNames = envivos
        .filter(e => selectedEnvivos.includes(e.id))
        .map(e => e.name)
        .join(', ');
      
      let message = `¡Hola ${card.clientUsername || 'Cliente'}! Gracias por tu compra 🎉\n\n`;
      message += `RESUMEN DE TU PEDIDO - Consolidado [${date}]\n`;
      message += `En Vivos: ${envivoNames}\n`;
      message += `━━━━━━━━━━━━━━━━━━\n`;
      
      // Para cards consolidadas, usar items consolidados
      const itemsToCopy = isConsolidated ? card.items : card.items || [];
      itemsToCopy.forEach((item, idx) => {
        const itemTotal = parseFloat(item.price || 0) * (item.quantity || 1);
        const quantity = item.quantity > 1 ? ` x${item.quantity}` : '';
        message += `${idx + 1}. ${item.productName}${quantity}\n`;
        message += `   💰 $${itemTotal.toLocaleString('es-CO')}\n`;
      });
      
      message += `━━━━━━━━━━━━━━━━━━\n`;
      message += `🛒 TOTAL DEL PEDIDO: $${card.totalAmount.toLocaleString('es-CO')}\n`;
      
      const totalPaid = card.totalPaid || 0;
      const remainingBalance = card.remainingAmount || 0;
      const percentage = card.totalAmount > 0 ? ((totalPaid / card.totalAmount) * 100) : 0;
      
      if (totalPaid > 0) {
        message += `💰 PAGOS REALIZADOS: $${totalPaid.toLocaleString('es-CO')}\n`;
        message += `📊 PORCENTAJE PAGADO: ${percentage.toFixed(1)}%\n`;
        message += `💳 SALDO PENDIENTE: $${remainingBalance.toLocaleString('es-CO')}\n`;
      } else {
        message += `💰 PAGOS REALIZADOS: $0\n`;
        message += `💳 SALDO PENDIENTE: $${card.totalAmount.toLocaleString('es-CO')}\n`;
      }
      
      message += `━━━━━━━━━━━━━━━━━━\n`;
      
      if (remainingBalance > 0) {
        message += `📌 Envía tu comprobante de pago para confirmar\n`;
        message += `💳 Saldo pendiente: $${remainingBalance.toLocaleString('es-CO')}`;
      } else {
        message += `✅ ¡PEDIDO COMPLETAMENTE PAGADO! 🎉\n`;
        message += `💰 Total pagado: $${totalPaid.toLocaleString('es-CO')}`;
      }

      await navigator.clipboard.writeText(message);
      
      // Usar un ID único para cards consolidadas
      const cardId = isConsolidated ? card.clientId : card.id;
      setCopiedOrders(prev => new Set([...prev, cardId]));
      setTimeout(() => {
        setCopiedOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(cardId);
          return newSet;
        });
      }, 2000);
      
      // debugLog('✅ Pedido consolidado copiado al portapapeles');
      
    } catch (err) {
      // errorLog('Error al copiar pedido:', err);
      setError('Error al copiar pedido: ' + err.message);
    }
  };
  
  // Función para copiar la card como imagen
  const copyOrderAsImage = async (card, isConsolidated = false) => {
    let minPaymentLine = null;
    let minPaymentLineOriginalDisplay = null;
    let originalWidth = null;
    let originalMaxWidth = null;
    let originalMinWidth = null;
    
    try {
      setCopyMenuOrderId(null);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Usar ID único según tipo de card
      const cardId = isConsolidated ? `consolidated-${card.clientId}` : card.id;
      const selector = isConsolidated ? `[data-order-id="${cardId}"]` : `[data-order-id="${card.id}"]`;
      const cardElement = document.querySelector(selector);
      
      if (!cardElement) {
        throw new Error('No se pudo encontrar la card de la orden');
      }
      
      // Guardar dimensiones originales de la card
      originalWidth = cardElement.style.width || '';
      originalMaxWidth = cardElement.style.maxWidth || '';
      originalMinWidth = cardElement.style.minWidth || '';
      
      // Aumentar temporalmente el ancho de la card para mejor calidad
      // Usar un ancho más amplio (450px) para que el texto tenga más espacio
      cardElement.style.width = '450px';
      cardElement.style.maxWidth = '450px';
      cardElement.style.minWidth = '450px';
      
      const hiddenElements = cardElement.querySelectorAll('.copy-menu-hidden');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });
      
      // Ocultar la línea de "Abono Mín. (con maleta)" antes de capturar
      minPaymentLine = cardElement.querySelector('.min-payment-line');
      if (minPaymentLine) {
        minPaymentLineOriginalDisplay = minPaymentLine.style.display || '';
        minPaymentLine.style.display = 'none';
      }
      
      // Esperar un momento adicional para asegurar que todo esté renderizado con el nuevo ancho
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const canvas = await html2canvas(cardElement, {
        backgroundColor: '#ffffff',
        scale: 5, // Aumentado a 5 para máxima calidad de imagen
        logging: false,
        useCORS: true,
        allowTaint: false,
        foreignObjectRendering: false, // Mejor renderizado de texto
        removeContainer: false,
        imageTimeout: 15000,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          // Asegurar que los estilos de texto se mantengan en el clon
          const clonedElement = clonedDoc.querySelector(selector);
          if (clonedElement) {
            // Aplicar el mismo ancho ampliado en el clon
            clonedElement.style.width = '450px';
            clonedElement.style.maxWidth = '450px';
            clonedElement.style.minWidth = '450px';
            
            // Ocultar también la línea de "Abono Mín. (con maleta)" en el clon
            const clonedMinPaymentLine = clonedElement.querySelector('.min-payment-line');
            if (clonedMinPaymentLine) {
              clonedMinPaymentLine.style.display = 'none';
            }
            
            // Forzar que los textos no se corten y asegurar que todo el contenido sea visible
            const textElements = clonedElement.querySelectorAll('*');
            textElements.forEach(el => {
              // Usar el estilo del elemento clonado, no del original
              const clonedComputedStyle = clonedDoc.defaultView.getComputedStyle(el);
              if (clonedComputedStyle.overflow === 'hidden' || clonedComputedStyle.textOverflow === 'ellipsis') {
                el.style.overflow = 'visible';
                el.style.textOverflow = 'clip';
                el.style.whiteSpace = 'normal';
              }
            });
            
            // Asegurar que el elemento no tenga altura máxima que corte contenido
            const clonedStyle = clonedDoc.defaultView.getComputedStyle(clonedElement);
            if (clonedStyle.maxHeight && clonedStyle.maxHeight !== 'none') {
              clonedElement.style.maxHeight = 'none';
            }
            
            // Asegurar que el overflow del elemento padre permita ver todo
            clonedElement.style.overflow = 'visible';
          }
        }
      });
      
      // Restaurar dimensiones originales de la card
      if (originalWidth) {
        cardElement.style.width = originalWidth;
      } else {
        cardElement.style.width = '';
      }
      if (originalMaxWidth) {
        cardElement.style.maxWidth = originalMaxWidth;
      } else {
        cardElement.style.maxWidth = '';
      }
      if (originalMinWidth) {
        cardElement.style.minWidth = originalMinWidth;
      } else {
        cardElement.style.minWidth = '';
      }
      
      // Generar blob con PNG sin compresión (calidad máxima)
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          
          // Restaurar elementos ocultos
          hiddenElements.forEach(el => {
            el.style.display = '';
          });
          
          // Restaurar la línea de "Abono Mín. (con maleta)"
          if (minPaymentLine && minPaymentLineOriginalDisplay !== null) {
            minPaymentLine.style.display = minPaymentLineOriginalDisplay;
          }
          
          setCopiedOrders(prev => new Set([...prev, cardId]));
          setTimeout(() => {
            setCopiedOrders(prev => {
              const newSet = new Set(prev);
              newSet.delete(cardId);
              return newSet;
            });
          }, 2000);
          
          // debugLog('✅ Imagen copiada al portapapeles');
          
        } catch (err) {
          // Restaurar elementos ocultos en caso de error
          hiddenElements.forEach(el => {
            el.style.display = '';
          });
          
          // Restaurar la línea de "Abono Mín. (con maleta)" en caso de error
          if (minPaymentLine && minPaymentLineOriginalDisplay !== null) {
            minPaymentLine.style.display = minPaymentLineOriginalDisplay;
          }
          
          // Restaurar dimensiones originales en caso de error
          if (cardElement) {
            if (originalWidth) {
              cardElement.style.width = originalWidth;
            } else {
              cardElement.style.width = '';
            }
            if (originalMaxWidth) {
              cardElement.style.maxWidth = originalMaxWidth;
            } else {
              cardElement.style.maxWidth = '';
            }
            if (originalMinWidth) {
              cardElement.style.minWidth = originalMinWidth;
            } else {
              cardElement.style.minWidth = '';
            }
          }
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const filename = isConsolidated ? `pedido-consolidado-${card.clientId}.png` : `pedido-${card.id}.png`;
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          
          // warningLog('⚠️ No se pudo copiar al portapapeles, descargando imagen:', err);
          
          alert('La imagen se ha descargado. Algunos navegadores no permiten copiar imágenes al portapapeles.');
        }
      }, 'image/png'); // Especificar explícitamente PNG sin compresión
      
    } catch (err) {
      // Restaurar la línea de "Abono Mín. (con maleta)" en caso de error general
      if (minPaymentLine && minPaymentLineOriginalDisplay !== null) {
        minPaymentLine.style.display = minPaymentLineOriginalDisplay;
      }
      
      // Restaurar dimensiones originales en caso de error general
      const cardId = isConsolidated ? `consolidated-${card.clientId}` : card.id;
      const selector = isConsolidated ? `[data-order-id="${cardId}"]` : `[data-order-id="${card.id}"]`;
      const cardElement = document.querySelector(selector);
      if (cardElement && (originalWidth !== null || originalMaxWidth !== null || originalMinWidth !== null)) {
        if (originalWidth) {
          cardElement.style.width = originalWidth;
        } else {
          cardElement.style.width = '';
        }
        if (originalMaxWidth) {
          cardElement.style.maxWidth = originalMaxWidth;
        } else {
          cardElement.style.maxWidth = '';
        }
        if (originalMinWidth) {
          cardElement.style.minWidth = originalMinWidth;
        } else {
          cardElement.style.minWidth = '';
        }
      }
      
      // errorLog('Error al copiar imagen:', err);
      setError('Error al copiar imagen: ' + err.message);
    }
  };
  
  // Cerrar menú de copiar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (copyMenuOrderId !== null && !event.target.closest('.copy-menu-container')) {
        setCopyMenuOrderId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [copyMenuOrderId]);

  // Limpiar blob URL al desmontar el componente
  useEffect(() => {
    return () => {
      if (receiptImageUrl) {
        URL.revokeObjectURL(receiptImageUrl);
      }
    };
  }, [receiptImageUrl]);
  
  // Función para abrir imagen de comprobante con autenticación
  const handleViewReceipt = async (receiptUrl) => {
    if (!receiptUrl) return;
    
    setReceiptImageLoading(true);
    setShowReceiptImageModal(true);
    setReceiptImageUrl(null);
    
    try {
      const token = localStorage.getItem('makana_token');
      if (!token) {
        setError('No hay token de autenticación');
        setReceiptImageLoading(false);
        return;
      }

      // Convertir URL HTTP a HTTPS para evitar problemas de Mixed Content
      let secureUrl = receiptUrl;
      if (receiptUrl.startsWith('http://')) {
        secureUrl = receiptUrl.replace('http://', 'https://');
      } else if (receiptUrl.startsWith('//')) {
        secureUrl = `https:${receiptUrl}`;
      } else if (!receiptUrl.startsWith('http')) {
        // Si es una ruta relativa, construir URL completa
        const baseUrl = window.location.origin;
        secureUrl = `${baseUrl}${receiptUrl.startsWith('/') ? '' : '/'}${receiptUrl}`;
      }

      // Hacer fetch a la imagen con autenticación
      const response = await fetch(secureUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar la imagen');
      }

      // Convertir a blob y crear URL temporal
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setReceiptImageUrl(blobUrl);
    } catch (err) {
      setError('Error al cargar el comprobante: ' + err.message);
      setShowReceiptImageModal(false);
    } finally {
      setReceiptImageLoading(false);
    }
  };

  // Limpiar blob URL al cerrar el modal
  const handleCloseReceiptModal = () => {
    if (receiptImageUrl) {
      URL.revokeObjectURL(receiptImageUrl);
    }
    setShowReceiptImageModal(false);
    setReceiptImageUrl(null);
    setReceiptImageLoading(false);
  };

  // Función para manejar el toggle de check_carry_on (empacado en maleta)
  // Función para abrir modal de pagos
  const handleOpenPaymentModal = async (card) => {
    setSelectedCardForPayments(card);
    setPaymentModalData({ totalAdvanceBalance: 0, advanceBalances: [], payments: [], loading: true });
    setShowPaymentModal(true);
    
    try {
      // Cargar bolsa de abonos (una vez por cada viaje único)
      let totalAdvanceBalance = 0;
      const advanceBalances = [];
      if (card.clientId && card.tripIds && card.tripIds.length > 0) {
        for (const tripId of card.tripIds) {
          try {
            const balanceData = await apiService.advancePayment.getBalance(card.clientId, tripId);
            const balance = parseFloat(
              balanceData.summary?.total_available || 
              balanceData.balance || 
              balanceData.available_balance || 
              0
            );
            if (balance > 0) {
              totalAdvanceBalance += balance;
              advanceBalances.push({ tripId, balance });
            }
          } catch (err) {
            // errorLog(`Error obteniendo bolsa para cliente ${card.clientUsername} en viaje ${tripId}:`, err);
          }
        }
      }
      
      // Cargar pagos de todas las órdenes del cliente
      const allPayments = [];
      if (card.orders && card.orders.length > 0) {
        for (const order of card.orders) {
          try {
            const payments = await apiService.payment.getByOrder(order.id);
            if (payments && Array.isArray(payments)) {
              allPayments.push(...payments);
            }
          } catch (err) {
            // errorLog(`Error cargando pagos para orden ${order.id}:`, err);
          }
        }
      }
      
      // Eliminar duplicados por ID
      const uniquePayments = Array.from(
        new Map(allPayments.map(p => [p.id, p])).values()
      );
      
        setPaymentModalData({
          totalAdvanceBalance: totalAdvanceBalance,
          advanceBalances: advanceBalances,
          payments: uniquePayments,
          loading: false
        });
    } catch (err) {
      // errorLog('Error cargando datos de pagos:', err);
      setPaymentModalData({
        totalAdvanceBalance: 0,
        advanceBalances: [],
        payments: [],
        loading: false
      });
    }
  };
  
  // Toggle acordeón de productos
  const toggleProductAccordion = (cardId) => {
    setOpenProductAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const handleToggleCheckCarryOn = async (itemId, currentCheckCarryOn) => {
    // Solo permitir si el item tiene carry_on = true
    const item = consolidatedOrderCards
      .flatMap(card => card.items)
      .find(i => i.id === itemId);
    
    if (!item || item.carry_on !== 1) {
      return; // No hacer nada si no tiene carry_on
    }

    // Prevenir múltiples clics mientras se actualiza
    if (updatingItems.has(itemId)) {
      return;
    }

    // Normalizar el valor actual (puede ser true, 1, false, 0, null, undefined)
    const isCurrentlyChecked = currentCheckCarryOn === true || currentCheckCarryOn === 1;
    const newValue = !isCurrentlyChecked;
    
    // debugLog('🔄 Toggle check_carry_on:', { itemId, currentCheckCarryOn, isCurrentlyChecked, newValue });
    
    try {
      // Agregar a items en actualización
      setUpdatingItems(prev => new Set([...prev, itemId]));

      // Llamar al endpoint
      const result = await apiService.order.updateCheckCarryOn(itemId, newValue);
      
      // Actualizar el estado local de los items
      setConsolidatedOrderCards(prevCards => {
        // console.log('🔄 ANTES de actualizar:', prevCards.find(c => 
        //   c.items.some(i => i.id === itemId)
        // )?.items.find(i => i.id === itemId));
        
        const updatedCards = prevCards.map(card => ({
          ...card,
          items: card.items.map(item => {
            if (item.id === itemId) {
              // Normalizar el valor de check_carry_on del endpoint a booleano
              const checkCarryOnValue = result.order_item.check_carry_on === true || 
                                       result.order_item.check_carry_on === 1 || 
                                       result.order_item.check_carry_on === 'true';
              const updatedItem = {
                ...item,
                check_carry_on: checkCarryOnValue,
                check_carry_on_updated_at: result.order_item.check_carry_on_updated_at
              };
              // console.log('🔄 Item actualizado:', { 
              //   itemId, 
              //   oldCheckCarryOn: item.check_carry_on, 
              //   newCheckCarryOn: updatedItem.check_carry_on,
              //   rawValue: result.order_item.check_carry_on,
              //   normalized: checkCarryOnValue
              // });
              return updatedItem;
            }
            return item;
          })
        }));
        
        // const updatedItem = updatedCards.find(c => 
        //   c.items.some(i => i.id === itemId)
        // )?.items.find(i => i.id === itemId);
        // console.log('🔄 DESPUÉS de actualizar:', {
        //   itemId,
        //   check_carry_on: updatedItem?.check_carry_on,
        //   type: typeof updatedItem?.check_carry_on,
        //   isTrue: updatedItem?.check_carry_on === true,
        //   isOne: updatedItem?.check_carry_on === 1,
        //   fullItem: updatedItem
        // });
        
        return updatedCards;
      });

      // debugLog('✅ check_carry_on actualizado exitosamente:', result);
    } catch (err) {
      // errorLog('Error al actualizar check_carry_on:', err);
      setError(err.message || 'Error al actualizar estado de empaque');
    } finally {
      // Remover de items en actualización
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };
  
  return (
    <div className="py-6 w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">📊 Vista Consolidada</h1>
      
      {/* Resumen Consolidado */}
      {selectedEnvivos.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-6">
          <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
            <TrendingUp className="text-green-600" size={20} />
            Resumen Consolidado
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 md:p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <Users className="text-blue-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-blue-800">Clientes</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-blue-900">
                {consolidatedData.totalClients}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 md:p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <ShoppingBag className="text-purple-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-purple-800">Órdenes</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-purple-900">
                {consolidatedData.totalOrders}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 md:p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <DollarSign className="text-green-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-green-800">Total</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-green-900 break-words">
                ${consolidatedData.totalRevenue.toLocaleString('es-CO')}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 p-3 md:p-4 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <DollarSign className="text-emerald-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-emerald-800">Abonado</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-emerald-900 break-words">
                ${consolidatedData.totalPaid.toLocaleString('es-CO')}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-3 md:p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <DollarSign className="text-orange-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-orange-800">Saldo</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-orange-900 break-words">
                ${consolidatedData.totalRemaining.toLocaleString('es-CO')}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-3 md:p-4 rounded-lg border border-indigo-200">
              <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                <ShoppingBag className="text-indigo-600" size={16} />
                <span className="text-xs md:text-sm font-medium text-indigo-800">Abonos</span>
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-bold text-indigo-900">
                {consolidatedData.totalPayments}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sección de Selección */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Columna izquierda: Selector de Viajes y Clientes con Bolsa */}
        <div className="space-y-6">
          {/* Selector de Viajes */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="text-blue-600" size={24} />
              Seleccionar Viajes
            </h2>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {trips.map((trip) => (
                <div key={trip.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedTrips.includes(trip.id)}
                    onChange={() => toggleTripSelection(trip.id)}
                    className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{trip.name?.replace(/-$/, '')}</div>
                    <div className="text-sm text-gray-600">
                      {trip.startDate && trip.endDate && `${new Date(trip.startDate).toLocaleDateString('es-CO')} - ${new Date(trip.endDate).toLocaleDateString('es-CO')}`}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trip.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                    trip.status === 'active' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {trip.status === 'planning' ? 'Planificando' :
                     trip.status === 'active' ? 'Activo' : 'Completado'}
                  </span>
                </div>
              ))}
              
              {trips.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar size={32} className="mx-auto mb-2" />
                  <p>No hay viajes disponibles</p>
                </div>
              )}
            </div>
          </div>

          {/* Clientes con Bolsa */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Wallet className="text-purple-600" size={24} />
              Clientes con Bolsa ({clientsWithBag.length})
            </h2>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {clientsWithBag.length > 0 ? (
                clientsWithBag.map((client, index) => (
                  <div 
                    key={`${client.username}-${index}`} 
                    className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{client.username}</div>
                    </div>
                    <div className="text-sm font-semibold text-purple-600">
                      ${client.bagAmount.toLocaleString('es-CO')}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Wallet size={32} className="mx-auto mb-2" />
                  <p>No hay clientes con bolsa</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* En Vivos de los Viajes Seleccionados */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Calendar className="text-purple-600" size={24} />
              En Vivos Seleccionados ({selectedEnvivos.length})
            </h2>
            {envivos.length > 0 && (
              <button
                onClick={selectAllEnvivos}
                disabled={isLoadingContinuously || loading}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isLoadingContinuously || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
                title="Seleccionar todos los envivos y cargarlos secuencialmente"
              >
                {isLoadingContinuously ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Cargando... ({continuousLoadingProgress.current}/{continuousLoadingProgress.total})
                  </span>
                ) : (
                  'Seleccionar Todos'
                )}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
            {envivos.map((envivo) => {
              const envivoColor = getEnvivoColor(envivo.id);
              return (
                <div
                  key={envivo.id}
                  className={`p-3 border ${selectedEnvivos.includes(envivo.id) && envivoColor ? envivoColor : 'border-gray-200 hover:bg-gray-50'} rounded-lg transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedEnvivos.includes(envivo.id)}
                      onChange={() => toggleEnvivoSelection(envivo.id)}
                      className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{envivo.name}</div>
                      <div className="text-sm text-gray-600">
                        {envivo.startTime && new Date(envivo.startTime).toLocaleString('es-CO')}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      envivo.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {envivo.status === 'active' ? 'ACTIVA' : 'CERRADA'}
                    </span>
                  </div>
                </div>
              );
            })}
            
            {envivos.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calendar size={32} className="mx-auto mb-2" />
                <p>No hay en vivos disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Cards de Órdenes */}
      {selectedEnvivos.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <ShoppingBag className="text-blue-600" size={24} />
              Órdenes Consolidadas por Cliente ({consolidatedOrderCards.length})
            </h2>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 mr-1">Ordenar por items:</span>
              <button
                onClick={() => setSortByItemsOrder('asc')}
                className={`p-2 rounded-lg border transition-colors ${
                  sortByItemsOrder === 'asc'
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
                }`}
                title="Ascendente (menos items primero)"
              >
                <ArrowUp size={18} />
              </button>
              <button
                onClick={() => setSortByItemsOrder('desc')}
                className={`p-2 rounded-lg border transition-colors ${
                  sortByItemsOrder === 'desc'
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200'
                }`}
                title="Descendente (más items primero)"
              >
                <ArrowDown size={18} />
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p>Cargando datos...</p>
            </div>
          ) : consolidatedOrderCards.length > 0 ? (
            <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {sortedOrderCards.map((card, cardIndex) => {
                const percentage = card.totalAmount > 0 ? ((card.totalPaid / card.totalAmount) * 100) : 0;
                
                return (
                  <div 
                    key={cardIndex}
                    data-order-id={`consolidated-${card.clientId}`}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50 relative break-inside-avoid mb-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-base font-bold">
                          {card.clientId || '?'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800">@{card.clientUsername}</h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getDeliveryStatusColor(card.deliveryStatus || '')}`}>
                              {getDeliveryStatusLabel(card.deliveryStatus || '')}
                            </span>
                          </div>
                          {card.client?.name && (
                            <p className="text-xs text-gray-600">{card.client.name}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* ✅ Acordeón de Productos */}
                    <div className="mb-3">
                      {/* Header del acordeón */}
                      <button
                        onClick={() => toggleProductAccordion(card.clientId)}
                        className="w-full flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 border border-gray-300"
                      >
                        <span className="font-semibold text-gray-700 flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          Items ({card.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)})
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${
                            openProductAccordions.has(card.clientId) ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {/* Contenido del acordeón (productos) */}
                      {openProductAccordions.has(card.clientId) && (
                        <div className="space-y-1 mt-2">
                          {card.items.map((item, index) => {
                            const itemTotal = parseFloat(item.price || 0) * (item.quantity || 1);
                            // Usar ID único para el key (combinación de id e index si no hay id único)
                            const itemKey = item.id ? `item-${item.id}` : `item-${card.clientId}-${index}`;
                            // Verificar el estado de check_carry_on para debugging
                            const isChecked = item.check_carry_on === true || item.check_carry_on === 1;

                            return (
                              <div
                                key={itemKey}
                                className={`flex items-start justify-between text-sm rounded px-2 py-2 border ${item.envivoColor || 'bg-white'}`}
                              >
                                <span className="text-gray-700" style={{ maxWidth: '60%', wordBreak: 'break-word' }}>
                                  {item.productName}
                                </span>
                                <div className="flex items-center gap-2 text-right flex-shrink-0">
                                  <span className="text-gray-500">x{item.quantity || 1}</span>
                                  <span className="text-blue-600 font-medium">
                                    ${itemTotal.toLocaleString('es-CO')}
                                  </span>
                                  {item.carry_on === 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleCheckCarryOn(item.id, item.check_carry_on);
                                      }}
                                      disabled={updatingItems.has(item.id)}
                                      style={{
                                        color: '#16a34a', // Siempre verde
                                        fontSize: isChecked ? '1rem' : '0.75rem',
                                        fontWeight: '500'
                                      }}
                                      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95 ${
                                        updatingItems.has(item.id) ? 'opacity-50 cursor-wait' : ''
                                      }`}
                                      title={
                                        isChecked
                                          ? '✅ Empacado en maleta - Toca para desmarcar'
                                          : '✈️ Viaja en maleta - Toca para marcar como empacado'
                                      }
                                    >
                                      <span style={{
                                        color: '#16a34a',
                                        display: 'inline-block'
                                      }}>
                                        {isChecked ? '✓' : '✈️'}
                                      </span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <div className="border-t border-blue-200 pt-3 mt-3">
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">💰 Total:</span>
                          <span className="text-lg font-bold text-blue-600">
                            ${card.totalAmount.toLocaleString('es-CO')}
                          </span>
                        </div>
                        
                        {/* Abono mínimo (solo si hay items en maleta) */}
                        {card.carryOnExtra > 0 && (
                          <div className="flex items-center justify-between min-payment-line">
                            <span className="text-sm font-medium text-gray-700">✈️ Abono Mín. (con maleta):</span>
                            <span className="text-xs font-semibold text-purple-600">
                              ${card.minPayment.toLocaleString('es-CO')}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">✅ Abonado:</span>
                          <span className="text-sm font-semibold text-green-600">
                            ${card.totalPaid.toLocaleString('es-CO')}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">⏳ Saldo:</span>
                          <span className={`text-sm font-semibold ${
                            card.remainingAmount <= 0 ? 'text-green-600' :
                            card.remainingAmount < card.totalAmount / 2 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            ${card.remainingAmount.toLocaleString('es-CO')}
                          </span>
                        </div>
                        
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              percentage >= 100 ? 'bg-green-500' :
                              percentage >= 50 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                        
                        {/* Porcentaje pagado */}
                        <div className="flex items-center justify-center mt-1">
                          <span className="text-xs font-medium text-gray-600">
                            {percentage.toFixed(1)}% pagado
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-xs text-gray-500">
                          {card.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)} items
                        </span>
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-center gap-2">
                          <div className="relative copy-menu-container">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCopyMenuOrderId(copyMenuOrderId === card.clientId ? null : card.clientId);
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Copiar pedido consolidado"
                            >
                              {copiedOrders.has(card.clientId) ? (
                                <>
                                  <Check size={14} className="text-green-600" />
                                  <span className="text-green-600">Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={14} />
                                  <span>Copiar</span>
                                </>
                              )}
                            </button>
                            
                            {/* Menú desplegable */}
                            {copyMenuOrderId === card.clientId && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyOrderToClipboard(card, true);
                                    setCopyMenuOrderId(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Copy size={14} />
                                  <span>Copiar Texto</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyOrderAsImage(card, true);
                                    setCopyMenuOrderId(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span>🖼️</span>
                                  <span>Copiar Imagen</span>
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Botón de Pagos */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPaymentModal(card);
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Ver pagos y bolsa de abonos"
                          >
                            <DollarSign size={14} />
                            <span>Pagos</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <ShoppingBag size={32} className="mx-auto mb-2" />
              <p>No hay órdenes en los en vivos seleccionados</p>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
          {error}
        </div>
      )}

      {/* Botón "Ir Arriba" de la derecha (solo escritorio lg) */}
      <button
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className="hidden lg:flex fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 z-50 items-center justify-center hover:scale-110"
        title="Volver arriba"
      >
        <ArrowUp size={24} />
      </button>

      {/* Buscador y botón "Ir Arriba" para iPad y Mobile (md hasta lg) */}
      <div className={`md:flex lg:hidden fixed bottom-4 right-4 z-50 flex items-center gap-3 transition-all duration-300 ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        {/* Campo de búsqueda expandible (se expande hacia la izquierda cuando se abre) */}
        <div className={`flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden transition-all duration-300 ${
          isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}>
          {/* Botón X para cerrar (a la izquierda del campo) */}
          {isSearchExpanded && (
            <button
              type="button"
              onClick={() => {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              title="Cerrar búsqueda"
            >
              <X size={20} className="font-bold" strokeWidth={3} />
            </button>
          )}
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
              if (event.key === 'Escape') {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onBlur={() => {
              // Cerrar si está vacío al perder el foco
              if (!mobileSearchTerm.trim()) {
                setIsSearchExpanded(false);
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            placeholder="Buscar en la página..."
            className={`px-4 py-3 text-sm text-gray-700 outline-none transition-all ${
              isSearchExpanded ? 'w-full flex-1' : 'w-0 hidden'
            }`}
            aria-label="Buscar en la página"
            autoFocus={isSearchExpanded}
          />
          {isSearchExpanded && (
            <button
              type="button"
              onClick={handleMobileQuickSearch}
              className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
              title="Buscar coincidencia"
            >
              <Search size={20} />
            </button>
          )}
        </div>

        {/* Botón de lupa (solo visible cuando NO está expandido) */}
        <button
          type="button"
          onClick={() => setIsSearchExpanded(!isSearchExpanded)}
          className={`bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50 ${
            isSearchExpanded ? 'hidden' : 'flex'
          }`}
          title="Buscar en la página"
        >
          <Search size={24} />
        </button>

        {/* Botón "Ir Arriba" */}
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50"
          title="Volver arriba"
        >
          <ArrowUp size={24} />
        </button>
      </div>

      {/* Buscador completo para Mobile (menor a md) */}
      <div className={`md:hidden fixed bottom-4 right-4 left-4 z-50 flex flex-col items-center gap-3 transition-transform duration-300 ease-in-out ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        <div className="flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden w-full max-w-sm mx-auto">
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
            }}
            placeholder="Buscar en la página..."
            className="flex-1 px-4 py-3 text-sm text-gray-700 outline-none"
            aria-label="Buscar en la página"
          />
          <button
            type="button"
            onClick={handleMobileQuickSearch}
            className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center"
            title="Buscar coincidencia"
          >
            <Search size={20} />
          </button>
        </div>

        {showScrollTop && (
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110"
            title="Volver arriba"
          >
            <ArrowUp size={24} />
          </button>
        )}
      </div>
      
      {/* Modal de Visualización de Pagos */}
      {showPaymentModal && selectedCardForPayments && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[95vh] flex flex-col">
            {/* Header fijo */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">
                💳 Pagos y Bolsa de Abonos - @{selectedCardForPayments.clientUsername}
              </h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedCardForPayments(null);
                  setPaymentModalData({ totalAdvanceBalance: 0, advanceBalances: [], payments: [], loading: false });
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido con scroll */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {paymentModalData.loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Cargando información de pagos...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Información de la orden consolidada */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="font-medium text-gray-800 mb-2">Detalles del Pedido Consolidado</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Total:</strong> ${selectedCardForPayments.totalAmount.toLocaleString('es-CO')}</p>
                      <p><strong>Abonado:</strong> ${selectedCardForPayments.totalPaid.toLocaleString('es-CO')}</p>
                      <p><strong>Saldo:</strong> ${selectedCardForPayments.remainingAmount.toLocaleString('es-CO')}</p>
                      <p><strong>Items:</strong> {selectedCardForPayments.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)}</p>
                    </div>
                  </div>

                  {/* Bolsa de abonos */}
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                      💼 Bolsa de Abonos
                    </h4>
                    <div className="text-sm">
                      {paymentModalData.totalAdvanceBalance > 0 ? (
                        <div>
                          <p className="text-purple-700 font-semibold text-lg">
                            ${paymentModalData.totalAdvanceBalance.toLocaleString('es-CO')} disponible
                          </p>
                          <p className="text-xs text-purple-600 mt-1">
                            Este saldo es un <strong>sobrante de pagos anteriores</strong> que aún no se ha aplicado a órdenes. <strong>NO está incluido</strong> en el total "Abonado" de la card.
                          </p>
                          {paymentModalData.advanceBalances && paymentModalData.advanceBalances.length > 1 && (
                            <div className="mt-2 text-xs text-purple-500">
                              (Desglosado por viaje: {paymentModalData.advanceBalances.map(b => `$${b.balance.toLocaleString('es-CO')} (Viaje ${b.tripId})`).join(', ')})
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-purple-600">No hay saldo disponible en bolsa de abonos.</p>
                      )}
                    </div>
                  </div>

                  {/* Lista de pagos realizados */}
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-3">
                      💳 Pagos Realizados ({paymentModalData.payments.length})
                    </h4>
                    {paymentModalData.payments.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {paymentModalData.payments.map((payment) => {
                          // Función helper para obtener la URL del comprobante
                          const getReceiptUrl = (payment) => {
                            return payment.receipt_url || 
                                   payment.receiptUrl || 
                                   payment.image_url || 
                                   payment.imageUrl || 
                                   payment.comprobante_url || 
                                   payment.comprobanteUrl || 
                                   payment.photo_url || 
                                   payment.photoUrl ||
                                   payment.foto_url ||
                                   payment.fotoUrl ||
                                   null;
                          };

                          const receiptUrl = getReceiptUrl(payment);

                          return (
                            <div 
                              key={payment.id} 
                              className="bg-white rounded-lg p-3 border border-blue-100"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-800">
                                      {payment.bank_name || payment.payment_type || 'Sin banco especificado'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-gray-600">
                                    <span className="text-green-600 font-semibold">
                                      ${parseFloat(payment.amount || 0).toLocaleString('es-CO')}
                                    </span>
                                    {payment.payment_date && (
                                      <>
                                        <span className="text-gray-400">•</span>
                                        <span>
                                          {new Date(payment.payment_date).toLocaleDateString('es-CO', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </span>
                                      </>
                                    )}
                                    {payment.created_at && !payment.payment_date && (
                                      <>
                                        <span className="text-gray-400">•</span>
                                        <span>
                                          {new Date(payment.created_at).toLocaleDateString('es-CO', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          })}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {receiptUrl && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleViewReceipt(receiptUrl);
                                    }}
                                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300 cursor-pointer"
                                    title="Ver comprobante de pago"
                                  >
                                    <ImageIcon size={14} />
                                    <ExternalLink size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-blue-600 text-sm">No se han registrado pagos para este pedido.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para mostrar imagen del comprobante */}
      {showReceiptImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[70] p-4"
          onClick={(e) => {
            // Cerrar solo si se hace clic en el overlay, no en el contenido
            if (e.target === e.currentTarget) {
              handleCloseReceiptModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ImageIcon className="text-blue-600" size={20} />
                Comprobante de Pago
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseReceiptModal();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Cerrar"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
              {receiptImageLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600">Cargando imagen...</p>
                </div>
              ) : receiptImageUrl ? (
                <img
                  src={receiptImageUrl}
                  alt="Comprobante de pago"
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  onError={() => {
                    setError('Error al mostrar la imagen');
                    handleCloseReceiptModal();
                  }}
                />
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No se pudo cargar la imagen</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consolidado;
